// Wave 10 (port-plan 24) — Generate Sales Scenarios: the standalone
// scenario-generation entry point for admins and super admins. Route
// access is gated in App.tsx (`effectiveNav`) off the nav registry's
// visibility rule, so managers deep-linking here bounce to the Teams
// dashboard; every backend endpoint re-enforces the same role gate.
//
// Plan 31 — teams. Super admins turn scenarios on per team in Manage
// Teams (`scenarios_enabled`). The page opens on the enabled "PNS Sales"
// team, else the first enabled team; disabled teams stay listed as
// "(Coming soon)" and can't be picked, and with none enabled the page
// shows a neutral empty state. Everything below the picker lives in
// ScenarioTeamWorkflow, remounted per team (`key={team.id}`) so no
// team-local state survives a switch; its team-scoped queries opt out of
// the global keepPreviousData, and every mutation carries the team it
// started for. If the server answers `scenarios_disabled` (a super
// admin turned the team off mid-session), the page says so, refetches
// the team list once and waits for the user to pick — no polling and no
// silent switch to another team.
//
// Step 1 — choose the team, end date and preceding days for an inclusive
// NZ calendar window (ADR 0017). The page lists every
// team member with their viable-transcript counts for that window,
// split into Total / Converted / Not converted side by side (converted
// comes straight from the API's per-member `converted_call_count`; not
// converted is total minus converted, clamped at 0).
//
// Step 2 — Identify scenarios: the outcome-split analysis. A button
// (shown once the pool is loaded) analyses a bounded evidence sample
// from both outcome buckets. The report shows the full-pool resistance
// distribution for converted vs not-converted calls, a short narrative
// contrasting them, and 3–5 expandable suggested-scenario tiles seeded
// by the most common resistance-label combinations mined from the
// not-converted calls. The analysis is a multi-minute LLM run, so it is
// enqueued as a background job and polled on a 2s cadence until a
// terminal state; failures surface loudly (the job's error string is
// shown verbatim) and changing the date clears a stale report/poll
// without touching the user's other selections.
//
// Per-tile "Generate & download": each suggested-scenario tile can be
// expanded (server-side background job) into a full, schema-valid
// roleplay scenario JSON file and downloaded. Tile state machine:
// Idle → Building → Ready | Error (Error → retry). Several tiles can
// build concurrently. The latest identification run and all tile states
// are persisted server-side, so leaving and returning to this view
// rehydrates Step 2 (report + tiles) without re-running the analysis;
// while any tile is building the run endpoint is polled on a 2s
// cadence. A Clear Scenarios button (visible whenever a completed run
// exists) deletes the run and its generations.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Download, Sparkles, Users } from "lucide-react";
import {
  api,
  isApiError,
  type ApiError,
  type ScenarioDistribution,
  type ScenarioGeneration,
  type ScenarioPreviewEnqueuedOut,
  type ScenarioRunOut,
  type SuggestedScenario,
  type TeamOut,
} from "../lib/api";
import { todayNzIso } from "../lib/nz-time";
import { buildUrl, parseUrl } from "../lib/router";
import { addDaysIso, retentionFloorIso } from "../lib/retention";
import { useConfirmDialog } from "../components/ConfirmDialog.helpers";
import { BulkScenariosPanel } from "./BulkScenariosPanel";
import { ScenarioSamplingCoverage } from "./ScenarioSamplingCoverage";
import { ScenarioExpiryWarning } from "./ScenarioExpiryWarning";
import { AssessmentEmptyState } from "../components/AssessmentEmptyState";
import {
  deriveScenarioWindow,
  fmtDay,
  formatScenarioWindow,
  isScenariosDisabledError,
  isTerminalPreviewStatus,
  memberCounts,
  pickDefaultScenarioTeam,
  previewPollMs,
  runPollMs,
  sameScenarioPreview,
  SCENARIO_POLICY_POLL_MS,
} from "./generateScenarios";

const MODE_BUTTON_CLASS =
  "hover-elevate rounded-md border border-input bg-background px-2.5 py-1 text-xs font-medium " +
  "text-foreground aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground";

const BUCKET_TITLES: Record<"converted" | "not_converted", string> = {
  converted: "Converted calls",
  not_converted: "Not-converted calls",
};

// The signature form-label pattern (DESIGN.md `label-caps`).
const LABEL_CLASS =
  "text-xs font-medium uppercase tracking-wide text-muted-foreground";

function errMessage(err: unknown): string {
  return (
    ((isApiError(err) || err instanceof Error) && err.message) ||
    "unknown error"
  );
}

/** One outcome bucket's resistance distribution: the bucket's exact
 * distinct-call total and its top measures (most common first). All
 * numbers here are computed server-side in SQL — never by the LLM. */
function DistributionPanel({
  bucket,
  distribution,
  outcomeLabel,
}: {
  bucket: "converted" | "not_converted";
  distribution: ScenarioDistribution;
  outcomeLabel?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-background p-4">
      <h4 className="text-sm font-semibold text-foreground">
        {outcomeLabel ? `${outcomeLabel} ${bucket === "converted" ? "detected" : "not detected"}` : BUCKET_TITLES[bucket]}
      </h4>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {distribution.total_calls} call
        {distribution.total_calls === 1 ? "" : "s"} with detected resistance
      </p>
      {distribution.top_measures.length > 0 ? (
        // Most common first; the count is the number of distinct calls
        // the measure appeared in (a call counts once per measure).
        <ol className="mt-3 space-y-1.5">
          {distribution.top_measures.map((m) => {
            const pct =
              distribution.total_calls > 0
                ? Math.round((m.call_count / distribution.total_calls) * 100)
                : 0;
            return (
              <li key={m.name} className="text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-foreground">{m.name}</span>
                  <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                    {m.call_count} call{m.call_count === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary/70"
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="mt-3 text-sm italic text-muted-foreground">
          No resistance measures detected in this bucket.
        </p>
      )}
    </div>
  );
}

/** Expanded-tile detail rows (empty fields skipped). */
const SUGGESTED_FIELD_LABELS: [keyof SuggestedScenario, string][] = [
  ["product", "Product"],
  ["scenario_type", "Scenario type"],
  ["customer_personality", "Customer personality"],
  ["pet_background", "Pet background"],
];

/** One expandable suggested-scenario tile. Collapsed: the title plus
 * the resistance measures (from the SQL-mined not-converted label
 * combination that seeded it, in dominance order). Expanded: the LLM's
 * scenario summary fields.
 *
 * The action row underneath drives this tile's generation state
 * machine: Idle ("Generate & download") → Building (disabled,
 * in-progress) → Ready ("Download scenario") or Error (message +
 * retry). `generation` is the server-persisted state; `isTriggering`
 * covers the moment between clicking and the server accepting the
 * job. */
function SuggestedScenarioTile({
  scenario,
  generation,
  isTriggering,
  tileError,
  onGenerate,
  onDownload,
  blocked,
}: {
  scenario: SuggestedScenario;
  generation: ScenarioGeneration | null;
  isTriggering: boolean;
  tileError: string | null;
  onGenerate: () => void;
  onDownload: () => void;
  blocked?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isBuilding = isTriggering || generation?.status === "building";
  const isReady = !isTriggering && generation?.status === "ready";
  const isError = !isTriggering && generation?.status === "error";
  return (
    <div className="rounded-md border border-border bg-card text-sm">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="hover-elevate flex w-full items-start justify-between gap-3 rounded-md p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div>
          <div className="font-medium text-foreground">
            {scenario.title || "Untitled scenario"}
          </div>
          {scenario.resistance_measures.length > 0 ? (
            <ol className="mt-1.5 flex flex-wrap gap-1.5">
              {scenario.resistance_measures.map((rm, i) => (
                <li
                  key={rm}
                  className="rounded-full border border-border bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground"
                >
                  {i + 1}. {rm}
                </li>
              ))}
            </ol>
          ) : null}
        </div>
        <ChevronDown
          aria-hidden="true"
          className={`mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open ? (
        <dl className="space-y-0.5 border-t border-border p-3">
          {SUGGESTED_FIELD_LABELS.map(([field, label]) => {
            const value = scenario[field];
            return typeof value === "string" && value ? (
              <div key={field} className="flex gap-1.5">
                <dt className="shrink-0 font-medium text-muted-foreground">
                  {label}:
                </dt>
                <dd className="text-foreground">{value}</dd>
              </div>
            ) : null;
          })}
        </dl>
      ) : null}
      <div className="border-t border-border p-3">
        <div className="flex flex-wrap items-center gap-3">
          {isReady ? (
            <button
              type="button"
              onClick={onDownload}
              className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Download scenario
              {generation?.filename ? (
                <span className="font-normal opacity-80">
                  ({generation.filename})
                </span>
              ) : null}
            </button>
          ) : (
            <button
              type="button"
              onClick={onGenerate}
              disabled={isBuilding || blocked}
              className="hover-elevate inline-flex items-center gap-1.5 rounded border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {isBuilding
                ? "Building…"
                : isError
                  ? "Try again"
                  : "Generate & download"}
            </button>
          )}
          {isBuilding ? (
            <span className="text-xs text-muted-foreground">
              Writing the full scenario file — this can take a minute.
            </span>
          ) : null}
        </div>
        {isError && generation?.error ? (
          <p className="mt-2 text-xs text-destructive">
            Generation failed: {generation.error}
          </p>
        ) : null}
        {tileError ? (
          <p className="mt-2 text-xs text-destructive">{tileError}</p>
        ) : null}
      </div>
    </div>
  );
}

/** Step 1's card: heading + description around whatever the page can
 * show (the team workflow, or a loading / notice / empty state). */
function PoolStepSection({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-foreground">
        Step 1 — Choose the transcript pool
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Pick the team, end date and preceding days. A balanced sample gives
        the AI evidence across the selected window.
      </p>
      {children}
    </section>
  );
}

/** Which team the page is on. `chosen` is pinned once set, so a later
 * team-list refetch can never swap the team out from under the user;
 * `blocked` means the server refused the team with `scenarios_disabled`
 * and the user must pick again. */
type TeamSelection =
  | { kind: "none" }
  | { kind: "chosen"; teamId: string }
  | { kind: "blocked"; teamId: string; teamName: string };

export function GenerateScenariosView() {
  const queryClient = useQueryClient();
  const teamsQ = useQuery({
    queryKey: ["teams", "all"],
    queryFn: () => api.listTeams(),
  });
  const teams = teamsQ.data ?? [];
  const enabledTeams = teams.filter((t) => t.scenarios_enabled);

  const [selection, setSelection] = useState<TeamSelection>({ kind: "none" });
  // Open on the URL's team (kept there so a reload returns to it) while it
  // is still enabled, else the flag-based default — the first time one
  // exists (adjusting state during render, React's pattern for state
  // derived from data).
  const [urlTeamId] = useState(() => parseUrl().team);
  const startTeam =
    enabledTeams.find((t) => t.id === urlTeamId) ??
    pickDefaultScenarioTeam(teams);
  if (selection.kind === "none" && startTeam) {
    setSelection({ kind: "chosen", teamId: startTeam.id });
  }

  const selectedTeam =
    selection.kind === "none"
      ? null
      : (teams.find((t) => t.id === selection.teamId) ?? null);
  // A chosen team that the refetched list now says is disabled is
  // treated exactly like a server refusal: notice, no workflow.
  const activeTeam =
    selection.kind === "chosen" && selectedTeam?.scenarios_enabled
      ? selectedTeam
      : null;
  const blockedTeamName =
    selection.kind === "blocked"
      ? selection.teamName
      : selectedTeam && !selectedTeam.scenarios_enabled
        ? selectedTeam.name
        : null;

  // Each mutation's effects are bound to the team it started for: UI
  // updates (and the download click) only happen while that team is
  // still the one on screen.
  const activeTeamId = activeTeam?.id ?? null;
  const activeTeamIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeTeamIdRef.current = activeTeamId;
  }, [activeTeamId]);
  // ADR 0009: the chosen team lives in the URL, so a reload reopens on it
  // (and its saved Bulk batch). Replace, not push — picking a team is not
  // a page the Back button should step through.
  useEffect(() => {
    if (!activeTeamId) return;
    const url = buildUrl("generate-scenarios", null, null, { team: activeTeamId });
    if (window.location.pathname + window.location.search !== url) {
      window.history.replaceState(window.history.state, "", url);
    }
  }, [activeTeamId]);
  const isActiveTeam = (teamId: string) => activeTeamIdRef.current === teamId;

  const handleScenariosDisabled = (team: TeamOut) => {
    setSelection((s) =>
      s.kind === "chosen" && s.teamId === team.id
        ? { kind: "blocked", teamId: team.id, teamName: team.name }
        : s,
    );
    // One refetch so the picker shows the team as "(Coming soon)";
    // cancelRefetch: false folds several refusals into one request.
    void queryClient.invalidateQueries(
      { queryKey: ["teams", "all"], exact: true },
      { cancelRefetch: false },
    );
  };

  const pickTeam = (teamId: string) => {
    if (enabledTeams.some((t) => t.id === teamId)) {
      setSelection({ kind: "chosen", teamId });
    }
  };

  const teamPicker = (
    <label className="flex flex-col gap-1">
      <span className={LABEL_CLASS}>Team</span>
      <select
        aria-label="Team"
        value={activeTeam?.id ?? ""}
        onChange={(e) => pickTeam(e.target.value)}
        className="rounded border border-input bg-background px-2 py-1.5 text-sm text-foreground"
      >
        {activeTeam ? null : (
          <option value="" disabled>
            Choose a team…
          </option>
        )}
        {teams.map((t) => (
          <option key={t.id} value={t.id} disabled={!t.scenarios_enabled}>
            {t.name}
            {t.scenarios_enabled ? "" : " (Coming soon)"}
          </option>
        ))}
      </select>
      <span className="text-xs text-muted-foreground">
        Availability is managed by super admins in Manage Teams.
      </span>
    </label>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
          Generate Sales Scenarios
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Create practice sales scenarios to coach your agents. Only admins
          and super admins can see this page.
        </p>
      </div>

      {teamsQ.data === undefined ? (
        <PoolStepSection>
          {teamsQ.error ? (
            <div className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              Failed to load teams: {errMessage(teamsQ.error)}
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">Loading teams…</p>
          )}
        </PoolStepSection>
      ) : activeTeam ? (
        <ScenarioTeamWorkflow
          key={activeTeam.id}
          team={activeTeam}
          teamPicker={teamPicker}
          isActiveTeam={isActiveTeam}
          onScenariosDisabled={handleScenariosDisabled}
        />
      ) : (
        <PoolStepSection>
          {blockedTeamName ? (
            <div
              role="status"
              className="mt-4 rounded border border-accent/40 bg-accent/10 p-3 text-sm text-accent-foreground"
            >
              Scenarios are disabled for &ldquo;{blockedTeamName}&rdquo;. Ask a
              super admin to enable them in Manage Teams
              {enabledTeams.length > 0 ? ", or pick another team below." : "."}
            </div>
          ) : null}
          {enabledTeams.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-end gap-4">
              {teamPicker}
            </div>
          ) : (
            <div className="mt-4">
              <AssessmentEmptyState
                icon={<Users className="h-5 w-5" />}
                headline="No teams are enabled for scenarios"
                subline={`A super admin can turn Scenarios on for a team in Manage Teams.${
                  teams.length > 0
                    ? ` Coming soon: ${teams.map((t) => t.name).join(", ")}.`
                    : ""
                }`}
              />
            </div>
          )}
        </PoolStepSection>
      )}
    </div>
  );
}

/** Everything that belongs to one team: the window, pool, analysis,
 * tiles, downloads and Clear. Mounted with `key={team.id}`, so switching
 * teams starts it from scratch. */
function ScenarioTeamWorkflow({
  team,
  teamPicker,
  isActiveTeam,
  onScenariosDisabled,
}: {
  team: TeamOut;
  teamPicker: ReactNode;
  isActiveTeam: (teamId: string) => boolean;
  onScenariosDisabled: (team: TeamOut) => void;
}) {
  const queryClient = useQueryClient();
  const clearDialog = useConfirmDialog();
  const clearRequested = useRef(false);
  const scopeEpoch = useRef(0);
  const scopeTouched = useRef(false);
  const bulkScopeKey = `scenario-bulk-scope:${team.id}`;
  const [savedBulkScope] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(bulkScopeKey) ?? "null");
      if (saved && [6, 12, 18, 24].includes(saved.days) && typeof saved.endDate === "string") {
        return { endDate: saved.endDate as string, days: saved.days as number };
      }
    } catch { /* Storage is optional. */ }
    return null;
  });
  const [bulk, setBulk] = useState(!!savedBulkScope);
  const [endDate, setEndDate] = useState(savedBulkScope?.endDate ?? "");
  const [lookbackDays, setLookbackDays] = useState(savedBulkScope?.days ?? 6);
  useEffect(() => {
    try {
      if (bulk) sessionStorage.setItem(bulkScopeKey, JSON.stringify({ endDate, days: lookbackDays }));
      else sessionStorage.removeItem(bulkScopeKey);
    } catch { /* Storage is optional. */ }
  }, [bulk, bulkScopeKey, endDate, lookbackDays]);

  // Every scenario call goes through this: a `scenarios_disabled` refusal
  // tells the page (once per call) and then follows the call's own error
  // path as usual.
  const guard = <T,>(call: Promise<T>): Promise<T> =>
    call.catch((err: unknown) => {
      if (isScenariosDisabledError(err)) onScenariosDisabled(team);
      throw err;
    });

  const window = endDate ? deriveScenarioWindow(endDate, lookbackDays) : null;

  const poolQ = useQuery({
    queryKey: ["scenario-pool", team.id, endDate, lookbackDays],
    queryFn: () => guard(lookbackDays === 6 ? api.scenarioPool(team.id, endDate) : api.scenarioPool(team.id, endDate, lookbackDays)),
    enabled: !!window,
    // Team-scoped: never show another team's (or another window's) pool
    // as a placeholder while this one loads (Plan 31).
    placeholderData: undefined,
    staleTime: 0,
    refetchInterval: SCENARIO_POLICY_POLL_MS,
  });

  // Step 2 (Identify scenarios): the outcome-split analysis. The
  // analysis is a long (multi-minute) LLM run, so it is enqueued as a
  // background job and polled — a synchronous request would hit the
  // deployment proxy's timeout and 502. The enqueue mutation runs cheap
  // validations (still failing loudly with a 422) and hands back a job
  // id; the status query then polls until a terminal state. Changing
  // the end date clears the job so a stale report / poll is never
  // shown. Both outcome buckets are always analysed.
  const [jobId, setJobId] = useState<string | null>(null);

  // Task #554 — Single select: when on, clicking rows in the pool table
  // narrows the analysed pool to just the selected people. Turning it
  // off clears every selection (whole-team pooling). With it on and
  // nobody selected, Identify scenarios is disabled with a hint — never
  // a silent fallback to everyone.
  const [singleSelect, setSingleSelect] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const toggleSingleSelect = () => {
    resetPreview();
    setBulk(false);
    setSingleSelect((on) => {
      if (on) setSelectedIds([]);
      return !on;
    });
  };
  const toggleMember = (id: string) => {
    if (!singleSelect) return;
    resetPreview();
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
  };
  const selectionEmpty = singleSelect && selectedIds.length === 0;

  const enqueueM = useMutation<
    ScenarioPreviewEnqueuedOut,
    ApiError,
    { teamId: string; endDate: string; agentIds: string[] | undefined; days: number; epoch: number }
  >({
    mutationFn: (v) =>
      guard(v.days === 6 ? api.scenarioPreview(v.teamId, v.endDate, v.agentIds)
        : api.scenarioPreview(v.teamId, v.endDate, v.agentIds, v.days)),
    onSuccess: (data, v) => {
      if (isActiveTeam(v.teamId) && v.epoch === scopeEpoch.current) setJobId(data.job_id);
    },
  });

  // Poll the job on a 2s cadence, stopping only on a confirmed terminal
  // status (same pattern as the assessment-run status pollers).
  const statusQ = useQuery({
    queryKey: ["scenario-preview", team.id, jobId],
    queryFn: () => guard(api.scenarioPreviewStatus(team.id, jobId!)),
    enabled: !!jobId,
    // Opt OUT of the app-wide `placeholderData: keepPreviousData` default
    // (lib/queryClient.ts). Clearing scenarios / changing the window resets
    // jobId to null, which flips this key to a disabled query; with the
    // global default the previous job's succeeded result would be served as
    // a placeholder forever (the disabled query never fetches to replace
    // it), so the report never leaves the screen. A job-status poll must
    // never show another job's data.
    placeholderData: undefined,
    refetchInterval: (q) => previewPollMs(q.state.data?.status),
  });

  // Belt-and-braces alongside `placeholderData: undefined` above: with no
  // live job there is no live report, whatever the query cache serves.
  const status = jobId ? statusQ.data : undefined;
  const isTerminalStatus = isTerminalPreviewStatus(status?.status);
  const jobFailed = status?.status === "failed" || status?.status === "dead";
  // The successfully-analysed report — present only once the job
  // succeeds (the backend serializes it into `result`).
  const previewData = status?.status === "succeeded" ? status.result : null;
  // In progress while the enqueue is in flight or a job exists that
  // hasn't reached a terminal state yet.
  const isPreviewRunning =
    enqueueM.isPending || (!!jobId && !isTerminalStatus);
  // Surface failures loudly: the enqueue's 422 detail, the job's own
  // error (verbatim — e.g. "ScenarioAnalysisError: …"), or
  // (defensively) a persistent poll failure so a hard error never
  // leaves the button spinning forever.
  const previewError = enqueueM.error
    ? errMessage(enqueueM.error)
    : jobFailed
      ? status?.error || "unknown error"
      : jobId && statusQ.error
        ? errMessage(statusQ.error)
        : null;

  const resetPreview = () => {
    scopeTouched.current = true;
    scopeEpoch.current += 1;
    enqueueM.reset();
    setJobId(null);
  };
  // Changing the window end date clears the stale report/poll (by
  // design) but must not reset the user's other selections.
  const changeEndDate = (value: string) => {
    setEndDate(value);
    resetPreview();
  };

  // The persisted latest run + per-tile generation state. This is what
  // rehydrates Step 2 after leaving and returning, and it is polled on
  // a 2s cadence while any tile is still building so concurrent
  // generations progress without a manual refresh.
  const runQ = useQuery({
    queryKey: ["scenario-run", team.id],
    queryFn: () => guard(api.scenarioRun(team.id)),
    // Team-scoped: never show another team's saved report (Plan 31).
    placeholderData: undefined,
    refetchInterval: (q) =>
      runPollMs((q.state.data as ScenarioRunOut | undefined)?.generations),
  });

  // Once the completed job's persisted state has been fetched, the saved
  // report becomes authoritative. Keeping its live result forever would
  // hide a later analysis saved by another admin for the same window.
  useEffect(() => {
    if (!previewData || !jobId) return;
    const completedJobId = jobId;
    const queryKey = ["scenario-run", team.id];
    void queryClient.invalidateQueries({ queryKey }).then(() => {
      const saved = queryClient.getQueryState<ScenarioRunOut>(queryKey);
      if (saved?.status === "success" && saved.data?.run && isActiveTeam(team.id)) {
        setJobId((current) => current === completedJobId ? null : current);
      }
    });
  }, [previewData, jobId, queryClient, team.id, isActiveTeam]);

  const persistedRun = runQ.data?.run ?? null;
  useEffect(() => {
    if (!persistedRun || scopeTouched.current || bulk) return;
    scopeTouched.current = true;
    setEndDate(persistedRun.window_end);
    setLookbackDays(persistedRun.lookback_days ?? 6);
    setSingleSelect(!!persistedRun.selected_agent_ids);
    setSelectedIds(persistedRun.selected_agent_ids ?? []);
  }, [persistedRun, bulk]);
  const persistedHasNewerPolicy = persistedRun && previewData &&
    (persistedRun.config_revision ?? -1) > (previewData.config_revision ?? -1);
  const candidateReport = persistedHasNewerPolicy ? persistedRun : previewData ?? persistedRun;
  const scopeMatches = candidateReport && (
    (!endDate && !scopeTouched.current) ||
    (candidateReport.window_end === endDate &&
      (candidateReport.lookback_days ?? 6) === lookbackDays &&
      JSON.stringify([...(candidateReport.selected_agent_ids ?? [])].sort()) ===
        JSON.stringify(singleSelect ? [...selectedIds].sort() : []) &&
      !selectionEmpty)
  );
  const report = !bulk && scopeMatches ? candidateReport : null;
  const reportMatchesPersisted = sameScenarioPreview(report, persistedRun);
  // The latest-run endpoints address tiles by index. Never attach a saved
  // file or allow generation until that index belongs to the shown report.
  const generations = reportMatchesPersisted ? (runQ.data?.generations ?? []) : [];
  const genByIndex = new Map(generations.map((g) => [g.scenario_index, g]));

  // Per-tile trigger/download errors and in-flight trigger flags
  // (`building` state proper comes back from the server).
  const [tileErrors, setTileErrors] = useState<Record<number, string>>({});
  const [triggering, setTriggering] = useState<Record<number, boolean>>({});

  const generateM = useMutation<
    unknown,
    ApiError,
    { teamId: string; index: number; runId?: string | null }
  >({
    mutationFn: (v) => guard(v.runId ? api.scenarioGenerate(v.teamId, v.index, v.runId) : api.scenarioGenerate(v.teamId, v.index)),
    onMutate: ({ index }) => {
      setTriggering((t) => ({ ...t, [index]: true }));
      setTileErrors((e) => {
        const next = { ...e };
        delete next[index];
        return next;
      });
    },
    onError: (err, { teamId, index }) => {
      if (!isActiveTeam(teamId)) return;
      setTileErrors((e) => ({ ...e, [index]: errMessage(err) }));
    },
    onSettled: (_data, _err, { teamId, index }) => {
      if (isActiveTeam(teamId)) {
        setTriggering((t) => ({ ...t, [index]: false }));
      }
      queryClient.invalidateQueries({ queryKey: ["scenario-run", teamId] });
    },
  });

  const downloadScenario = async (index: number) => {
    const teamId = team.id;
    const epoch = scopeEpoch.current;
    setTileErrors((e) => {
      const next = { ...e };
      delete next[index];
      return next;
    });
    try {
      const { blob, filename } = await guard(
        report?.run_id ? api.scenarioDownload(teamId, index, report.run_id) : api.scenarioDownload(teamId, index),
      );
      // The file belongs to the team the click started for: if the user
      // has switched teams meanwhile, drop it rather than save it while
      // another team is on screen.
      if (!isActiveTeam(teamId) || epoch !== scopeEpoch.current) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (!isActiveTeam(teamId) || epoch !== scopeEpoch.current) return;
      setTileErrors((e) => ({
        ...e,
        [index]: `Download failed: ${errMessage(err)}`,
      }));
    }
  };

  // Clear Scenarios: deletes the team's identification runs (tile
  // generations cascade) and resets the local preview state so Step 2
  // returns to its initial state.
  const clearM = useMutation<void, ApiError, string>({
    mutationFn: (teamId) => guard(api.clearScenarioRuns(teamId)),
    onSuccess: async (_data, teamId) => {
      // Reset EVERY client state feeding the Step 2 report in one place,
      // deterministically, so the UI flips to its initial state instantly
      // (no refetch race, no page refresh needed) — but only while the
      // cleared team is still the one on screen.
      if (isActiveTeam(teamId)) {
        resetPreview();
        setTileErrors({});
        setTriggering({});
      }
      // The cache work below always targets the team that was cleared,
      // whichever team is on screen now.
      // Drop the cached preview-poll results so the live in-session report
      // can never be resurrected from the query cache.
      queryClient.removeQueries({
        queryKey: ["scenario-preview", teamId],
      });
      // invalidateQueries alone is not deterministic: a scenario-run fetch
      // already in flight when the delete lands (the 2s building poll)
      // dedupes the invalidation onto that pre-delete request, and the
      // stale run lands back in the cache as fresh. So cancel any in-flight
      // fetch, seed the cache empty (the report disappears at once), then
      // invalidate to confirm against the server.
      await queryClient.cancelQueries({
        queryKey: ["scenario-run", teamId],
      });
      queryClient.setQueryData(["scenario-run", teamId], {
        run: null,
        generations: [],
      } satisfies ScenarioRunOut);
      queryClient.invalidateQueries({
        queryKey: ["scenario-run", teamId],
      });
    },
  });

  async function confirmClear() {
    if (clearRequested.current || clearM.isPending) return;
    clearRequested.current = true;
    try {
      const confirmed = await clearDialog.confirm({
        title: `Clear scenarios for ${team.name}?`,
        message: `This permanently deletes ${team.name}’s saved scenario reports and all generated scenario files. Source calls and transcripts are not deleted. Download any files you want to keep first. This cannot be undone.`,
        confirmLabel: "Delete reports and files",
        destructive: true,
      });
      if (confirmed && isActiveTeam(team.id)) clearM.mutate(team.id);
    } finally {
      clearRequested.current = false;
    }
  }

  return (
    <>
      {clearDialog.element}
      <PoolStepSection>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          {teamPicker}

          <div className="flex flex-col gap-1">
            <label className="flex flex-col gap-1">
              <span className={LABEL_CLASS}>
                Window end date (NZ time, inclusive)
              </span>
              <input
                type="date"
                aria-label="Window end date"
                value={endDate}
                min={addDaysIso(retentionFloorIso(todayNzIso()), lookbackDays)}
                max={todayNzIso()}
                onChange={(e) => changeEndDate(e.target.value)}
                className="rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
            </label>
            <label className="text-xs text-muted-foreground">
              Covers that day plus{" "}
              <select
                aria-label="Days before end date"
                value={lookbackDays}
                onChange={(e) => { setLookbackDays(Number(e.target.value)); resetPreview(); }}
                className="rounded border border-input bg-background px-2 py-1"
              >
                {[6, 12, 18, 24].map((days) => <option key={days} value={days}>{days}</option>)}
              </select>{" "}
              days before it
            </label>
          </div>

          {window ? (
            <div className="pb-5 text-sm text-foreground">
              Window:{" "}
              <span className="font-medium">
                {formatScenarioWindow(endDate, lookbackDays)}
              </span>
              <span className="block text-xs text-muted-foreground">
                {window.start} through {window.end} · {lookbackDays + 1} NZ days, inclusive
              </span>
            </div>
          ) : null}
        </div>

        {window ? (
          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-sm font-semibold text-foreground">
                Viable transcripts per person
              </h3>
              <button
                type="button"
                aria-pressed={!singleSelect && !bulk}
                className={MODE_BUTTON_CLASS}
                onClick={() => {
                  resetPreview();
                  setBulk(false);
                  setSingleSelect(false);
                  setSelectedIds([]);
                }}
              >
                Whole team
              </button>
              <button
                type="button"
                aria-pressed={bulk}
                className={MODE_BUTTON_CLASS}
                onClick={() => {
                  resetPreview();
                  setBulk(true);
                  setSingleSelect(false);
                  setSelectedIds([]);
                }}
              >
                Bulk
              </button>
              <button
                type="button"
                aria-pressed={singleSelect}
                onClick={toggleSingleSelect}
                className={MODE_BUTTON_CLASS}
              >
                Single select
              </button>
              {singleSelect ? (
                <span className="text-xs text-muted-foreground">
                  Click people to include only their transcripts.
                </span>
              ) : null}
            </div>
            {poolQ.data ? <p className="mt-2 text-xs text-muted-foreground">
              {singleSelect ? "Selected people" : "Whole team"}:{" "}
              {poolQ.data.members
                .filter((m) => !singleSelect || selectedIds.includes(m.dialpad_user_id))
                .reduce((sum, m) => sum + m.viable_call_count, 0)} viable calls in this period.
              {" "}The table lists all team members so you can adjust the selection.
            </p> : null}
            {poolQ.isLoading ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Counting viable transcripts…
              </p>
            ) : poolQ.error ? (
              <div className="mt-2 rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                Failed to load the transcript pool: {errMessage(poolQ.error)}
              </div>
            ) : poolQ.data && poolQ.data.members.length === 0 ? (
              <p className="mt-2 text-sm italic text-muted-foreground">
                This team has no members yet.
              </p>
            ) : poolQ.data ? (
              <table className="mt-2 w-full max-w-lg text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-1.5 pr-4 font-medium">Team member</th>
                    <th className="py-1.5 pl-4 text-right font-medium">
                      Total transcripts
                    </th>
                    <th className="py-1.5 pl-4 text-right font-medium">
                      {poolQ.data.outcome_label ? `${poolQ.data.outcome_label} detected` : "Converted"}
                    </th>
                    <th className="py-1.5 pl-4 text-right font-medium">
                      {poolQ.data.outcome_label ? `${poolQ.data.outcome_label} not detected` : "Not converted"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {poolQ.data.members.map((m) => {
                    const counts = memberCounts(m);
                    const cell = (count: number) =>
                      `py-1.5 pl-4 text-right tabular-nums ${
                        count === 0
                          ? "text-muted-foreground"
                          : "text-foreground"
                      }`;
                    const isSelected =
                      singleSelect && selectedIds.includes(m.dialpad_user_id);
                    return (
                      <tr
                        key={m.dialpad_user_id}
                        onClick={() => toggleMember(m.dialpad_user_id)}
                        aria-selected={singleSelect ? isSelected : undefined}
                        className={`border-b border-border ${
                          singleSelect ? "cursor-pointer hover:bg-muted/60" : ""
                        } ${isSelected ? "bg-primary/10" : ""}`}
                      >
                        <td className="py-1.5 pr-4 text-foreground">
                          <span className="inline-flex items-center gap-2">
                            {singleSelect ? (
                              <Check
                                aria-hidden="true"
                                className={`h-3.5 w-3.5 ${
                                  isSelected ? "text-primary" : "text-transparent"
                                }`}
                              />
                            ) : null}
                            {m.name}
                          </span>
                        </td>
                        <td className={cell(counts.total)}>{counts.total}</td>
                        <td className={cell(counts.converted)}>
                          {counts.converted}
                        </td>
                        <td className={cell(counts.notConverted)}>
                          {counts.notConverted}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </div>
        ) : (
          <p className="mt-6 text-sm italic text-muted-foreground">
            Pick an end date to see each team member&rsquo;s viable
            transcripts for the window.
          </p>
        )}
      </PoolStepSection>

      {/* Step 2 — outcome-split Identify scenarios. Shown once the pool
          is loaded (so the analysis always runs against a window the
          admin can see above) OR when a persisted run exists
          (rehydration — no date re-entry needed to get back to the
          report and its tiles). Always covers both outcome buckets. */}
      {bulk && window && poolQ.data && (
        <BulkScenariosPanel key={`${team.id}:${endDate}:${lookbackDays}`}
          teamId={team.id} endDate={endDate} days={lookbackDays} members={poolQ.data.members} />
      )}
      {!bulk && ((window && poolQ.data) || report) ? (
        <section className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h2 className="text-base font-semibold text-foreground">
            Step 2 — Identify scenarios
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Analyse a representative sample of evidence in this window,
            split by outcome: which resistance came up on converted vs
            not-converted calls, and which scenarios are worth practising.
          </p>

          {window && poolQ.data ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  enqueueM.mutate({
                    teamId: team.id,
                    endDate,
                    agentIds: singleSelect ? selectedIds : undefined,
                    days: lookbackDays,
                    epoch: scopeEpoch.current,
                  })
                }
                disabled={isPreviewRunning || selectionEmpty}
                className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {isPreviewRunning ? "Analysing…" : "Identify scenarios"}
              </button>
              {selectionEmpty && !isPreviewRunning ? (
                <span className="text-sm text-muted-foreground">
                  Single select is on — select at least one team member
                  above, or turn it off to analyse the whole team.
                </span>
              ) : null}
              {isPreviewRunning ? (
                <span className="text-sm text-muted-foreground">
                  Reading a balanced evidence sample — this can take a
                  couple of minutes.
                </span>
              ) : null}
            </div>
          ) : null}

          {previewError ? (
            <div className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              The analysis failed: {previewError}
            </div>
          ) : null}

          {report ? (
            <div className="mt-4 space-y-4">
              <ScenarioSamplingCoverage report={report} />
              <p className="text-sm text-muted-foreground">
                {report.sampling ? "Population: " : "Analysed "}
                <span className="font-medium text-foreground">
                  {report.sampling?.resistance_detected ?? report.calls_analysed}
                </span>{" "}
                call{(report.sampling?.resistance_detected ?? report.calls_analysed) === 1 ? "" : "s"} with detected
                resistance ({report.converted_count} {report.outcome_label ? `${report.outcome_label} detected` : "converted"},{" "}
                {report.not_converted_count} {report.outcome_label ? `${report.outcome_label} not detected` : "not converted"}) for the window{" "}
                {fmtDay(report.window_start)} – {fmtDay(report.window_end)}.
              </p>
              {/* Thin-pool notes: the analysis degraded gracefully
                  instead of failing — say how. */}
              {report.notes.length > 0 ? (
                <ul className="space-y-1 rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
                  {report.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              ) : null}
              {/* Outcome-split distributions — exact SQL-computed
                  counts, side by side. */}
              <div className="grid gap-4 sm:grid-cols-2">
                <DistributionPanel
                  outcomeLabel={report.outcome_label}
                  bucket="converted"
                  distribution={report.converted}
                />
                <DistributionPanel
                  outcomeLabel={report.outcome_label}
                  bucket="not_converted"
                  distribution={report.not_converted}
                />
              </div>
              {report.narrative ? (
                <div className="rounded-md border border-border bg-background p-4">
                  <h4 className="text-sm font-semibold text-foreground">
                    What the calls show
                  </h4>
                  <p className="mt-2 text-sm text-foreground">
                    {report.narrative}
                  </p>
                </div>
              ) : null}
              {/* Suggested scenarios: one tile per resistance-label
                  combination mined (in SQL) from the not-converted
                  calls, most common first. Each tile can generate +
                  download its full scenario JSON file. */}
              {report.suggested_scenarios.length > 0 ? (
                <div className="rounded-md border border-border bg-background p-4">
                  <h4 className="text-sm font-semibold text-foreground">
                    Suggested scenarios
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Built from the most common resistance combinations on
                    calls without the selected outcome detected. Expand a tile for the scenario
                    summary, or generate its full scenario file.
                  </p>
                  <div className="mt-3 space-y-3">
                    <ScenarioExpiryWarning expiresAt={report.expires_at} />
                    {!reportMatchesPersisted && (
                      <p role="status" className="text-sm text-muted-foreground">Refreshing scenarios…</p>
                    )}
                    {runQ.data?.generation_blocked_reason && (
                      <p role="alert" className="text-sm text-destructive">
                        {runQ.data.generation_blocked_reason} Saved files remain downloadable.
                      </p>
                    )}
                    {report.suggested_scenarios.map((s, i) => (
                      <SuggestedScenarioTile
                        blocked={!reportMatchesPersisted || !!runQ.data?.generation_blocked_reason}
                        key={i}
                        scenario={s}
                        generation={genByIndex.get(i) ?? null}
                        isTriggering={!!triggering[i]}
                        tileError={tileErrors[i] ?? null}
                        onGenerate={() => generateM.mutate({ teamId: team.id, index: i, runId: report.run_id })}
                        onDownload={() => void downloadScenario(i)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
              {/* Clear Scenarios: visible whenever a completed
                  identification run exists. Deletes the persisted run
                  and every tile's generation. */}
              {persistedRun && !isPreviewRunning ? (
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void confirmClear()}
                    disabled={clearM.isPending}
                    className="hover-elevate rounded border border-destructive/30 bg-background px-3 py-1.5 text-sm font-medium text-destructive disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                  >
                    {clearM.isPending ? "Clearing…" : "Clear scenarios"}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    Removes this report and all generated scenario files.
                  </span>
                  {clearM.error ? (
                    <span className="text-xs text-destructive">
                      Clearing failed: {errMessage(clearM.error)}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
